import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
// hooks
import useAuth from 'src/hooks/useAuth';
// components
import MyAvatar from 'src/components/MyAvatar';
import Icon from 'src/components/universe/Icon';
import ContextSheet from 'src/components/universe/layers/ContextSheet';
import {
  BRAND_LOGO_WHITE,
  HIT,
  MOTION,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT,
  accentOf,
  accentTint,
  focusRingSx,
  hairlineOf
} from 'src/theme/universe/brand';
//
import VendorAccountPanel, { accountName } from './VendorAccountMenu';
import { ROLE_WORD, barFor, homeFor, isActive } from './vendorNav';

// ---------------------------------------------------------------------------
// THE PHONE AND TABLET SHELL (DESIGN.md §12, below 900px): a 56px night bar
// at the top with the lock-up and the account, and the 5 + More bar at the
// foot — never six slots, never a hamburger drawer over the page. More rises
// as a context sheet holding the destinations that did not fit a slot and the
// account (the theme, the profile pages, signing out). A type with fewer
// destinations shows what it has plus More, so the account is always one
// press away. Both bars sit inside the device's safe areas.
// ---------------------------------------------------------------------------

export const TOP_BAR = 56;
export const BOTTOM_BAR = 60;

const WHITE_SOFT = alpha(UNIVERSE.white, 0.7);

const ringSx = {
  outline: 'none',
  boxShadow: `0 0 0 2px ${UNIVERSE.night}, 0 0 0 4px ${UNIVERSE.light}`
};

const night = {
  bgcolor: UNIVERSE.night,
  backgroundImage: `radial-gradient(420px 160px at 0% 0%, ${alpha(
    UNIVERSE.light,
    0.2
  )}, transparent 70%)`,
  color: UNIVERSE.white
};

// ---------------------------------------------------------------------------

VendorTopBar.propTypes = { onAccount: PropTypes.func };

export function VendorTopBar({ onAccount }) {
  const { user, type } = useAuth();
  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        height: `calc(${TOP_BAR}px + env(safe-area-inset-top))`,
        pt: 'env(safe-area-inset-top)',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...night,
        borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}
    >
      <Box
        component={RouterLink}
        to={homeFor(type)}
        aria-label="LingoYou Universe, home"
        sx={{
          display: 'inline-flex',
          lineHeight: 0,
          borderRadius: `${RADIUS.small}px`,
          '&:focus-visible': ringSx
        }}
      >
        <Box
          component="img"
          src={BRAND_LOGO_WHITE}
          alt="LingoYou Universe"
          sx={{ height: 26, width: 'auto', display: 'block' }}
        />
      </Box>
      <ButtonBase
        onClick={onAccount}
        aria-label={`Account: ${accountName(user)}`}
        sx={{
          width: HIT.touch,
          height: HIT.touch,
          borderRadius: '50%',
          '&.Mui-focusVisible, &:focus-visible': ringSx
        }}
      >
        <Box sx={{ p: '2px', borderRadius: '50%', backgroundImage: UNIVERSE.gradient }}>
          <Box sx={{ p: '2px', borderRadius: '50%', bgcolor: UNIVERSE.night, lineHeight: 0 }}>
            <MyAvatar sx={{ width: 30, height: 30 }} />
          </Box>
        </Box>
      </ButtonBase>
    </Box>
  );
}

// ---------------------------------------------------------------------------

Slot.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  active: PropTypes.bool,
  to: PropTypes.string,
  onClick: PropTypes.func
};

function Slot({ label, icon, active = false, to, onClick }) {
  return (
    <ButtonBase
      {...(to ? { component: RouterLink, to } : { onClick })}
      aria-current={active ? 'page' : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        height: BOTTOM_BAR,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.375,
        textDecoration: 'none',
        color: active ? UNIVERSE.white : WHITE_SOFT,
        '&:hover': { color: UNIVERSE.white },
        '&.Mui-focusVisible, &:focus-visible': { ...ringSx, borderRadius: `${RADIUS.overlay}px` }
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 30,
          display: 'grid',
          placeItems: 'center',
          borderRadius: `${RADIUS.small}px`,
          backgroundImage: active ? UNIVERSE.gradient : 'none',
          boxShadow: active ? `0 6px 16px ${alpha(UNIVERSE.light, 0.3)}` : 'none',
          transition: `box-shadow ${MOTION.standard}ms`
        }}
      >
        <Icon name={icon} size={22} variant={active ? 'Bold' : 'Linear'} />
      </Box>
      <Typography
        component="span"
        noWrap
        sx={{
          maxWidth: 1,
          px: 0.5,
          fontFamily: UNIVERSE_FONT,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.2,
          color: 'inherit'
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

// ---------------------------------------------------------------------------

VendorBottomBar.propTypes = {
  moreOpen: PropTypes.bool,
  onMore: PropTypes.func
};

export function VendorBottomBar({ moreOpen = false, onMore }) {
  const { pathname } = useLocation();
  const { type } = useAuth();
  const { slots, more } = barFor(type);
  const moreActive = moreOpen || more.some((item) => isActive(item, pathname));

  return (
    <Box
      component="nav"
      aria-label="Vendor portal"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        height: `calc(${BOTTOM_BAR}px + env(safe-area-inset-bottom))`,
        pb: 'env(safe-area-inset-bottom)',
        px: 0.5,
        display: 'flex',
        alignItems: 'stretch',
        ...night,
        backgroundImage: 'none',
        borderTop: '1px solid rgba(255,255,255,0.08)'
      }}
    >
      {slots.map((item) => (
        <Slot
          key={item.key}
          label={item.short || item.label}
          icon={item.icon}
          to={item.path}
          active={!moreOpen && isActive(item, pathname)}
        />
      ))}
      <Slot label="More" icon="More" onClick={onMore} active={moreActive} />
    </Box>
  );
}

// ---------------------------------------------------------------------------

MoreSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export function MoreSheet({ open, onClose }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { user, type } = useAuth();
  const { more } = barFor(type);
  return (
    <ContextSheet
      open={open}
      onClose={onClose}
      title={accountName(user)}
      meta={[ROLE_WORD[type], user?.email].filter(Boolean).join(' · ')}
    >
      {more.length > 0 && (
        <Stack spacing={0.25} sx={{ pb: 1.5, mb: 1, borderBottom: `1px solid ${hairlineOf(theme)}` }}>
          {more.map((item) => {
            const active = isActive(item, pathname);
            return (
              <ButtonBase
                key={item.key}
                component={RouterLink}
                to={item.path}
                onClick={onClose}
                aria-current={active ? 'page' : undefined}
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  minHeight: HIT.touch,
                  px: 1.25,
                  borderRadius: `${RADIUS.control}px`,
                  fontFamily: UNIVERSE_FONT,
                  fontSize: 15,
                  fontWeight: active ? 600 : 500,
                  color: active ? accentOf(theme) : 'text.primary',
                  bgcolor: active ? accentTint(theme, 'selected') : 'transparent',
                  boxShadow: active ? `inset 2px 0 0 ${accentOf(theme)}` : 'none',
                  '& svg': { color: active ? accentOf(theme) : 'text.secondary' },
                  '&:focus-visible': focusRingSx(theme)
                }}
              >
                <Icon name={item.icon} size="md" variant={active ? 'Bold' : 'Linear'} />
                {item.label}
              </ButtonBase>
            );
          })}
        </Stack>
      )}
      <VendorAccountPanel onClose={onClose} showIdentity={false} />
    </ContextSheet>
  );
}

/** The phone shell's state: which sheet is up. */
export function usePhoneSheets() {
  const [open, setOpen] = useState(false);
  return { open, show: () => setOpen(true), hide: () => setOpen(false) };
}
